// OPS-28 #1 — the review screens' view of the answer queue's kept failures.
//
// A React subscription to lib/answerQueue's module-level list, so the Submit
// guard re-renders the moment a save is abandoned (or lands on retry) — the
// queue outlives every candidate route, which is exactly why the screens
// cannot hold this state themselves.
import { useCallback, useMemo, useState, useSyncExternalStore } from "react";

import {
  answerSaveFailuresSnapshot,
  answerSaveRefusalsSnapshot,
  retryAnswerSaveFailures,
  subscribeAnswerSaveFailures,
  type AnswerSaveFailure,
  type AnswerSaveRefusal,
} from "@/lib/answerQueue";

export interface AnswerSaveFailures {
  /** The attempt's abandoned saves, oldest first (empty = nothing lost). */
  failures: AnswerSaveFailure[];
  /** R13: saves the server refused twice — never holding the Submit, shown
   *  as a warning. */
  refused: AnswerSaveRefusal[];
  /** Re-send every one of them, in order. Resolves with how many remain. */
  retry: () => Promise<number>;
  retrying: boolean;
}

/** A section's positions, inclusive. A sectioned review counts only the
 *  failures inside it: an earlier section is locked, so its failures can no
 *  longer be saved — holding this section's button on them would be a block
 *  the candidate cannot clear. */
export interface PositionRange {
  start: number;
  end: number;
}

export function useAnswerSaveFailures(
  attemptId: string | null,
  range?: PositionRange | null,
): AnswerSaveFailures {
  const all = useSyncExternalStore(
    subscribeAnswerSaveFailures,
    answerSaveFailuresSnapshot,
    answerSaveFailuresSnapshot,
  );
  const allRefused = useSyncExternalStore(
    subscribeAnswerSaveFailures,
    answerSaveRefusalsSnapshot,
    answerSaveRefusalsSnapshot,
  );
  const start = range?.start;
  const end = range?.end;
  const failures = useMemo(
    () => (attemptId ? all.filter((f) => inScope(f, attemptId, start, end)) : []),
    [all, attemptId, start, end],
  );
  const refused = useMemo(
    () => (attemptId ? allRefused.filter((r) => inScope(r, attemptId, start, end)) : []),
    [allRefused, attemptId, start, end],
  );
  const [retrying, setRetrying] = useState(false);
  const retry = useCallback(async () => {
    if (!attemptId) return 0;
    setRetrying(true);
    try {
      return await retryAnswerSaveFailures(attemptId);
    } finally {
      setRetrying(false);
    }
  }, [attemptId]);
  return { failures, refused, retry, retrying };
}

function inScope(
  f: { attemptId: string; position: number },
  attemptId: string,
  start: number | undefined,
  end: number | undefined,
): boolean {
  return (
    f.attemptId === attemptId &&
    (start === undefined || f.position >= start) &&
    (end === undefined || f.position <= end)
  );
}
